import React, { useState } from 'react';
import { push, ref } from 'firebase/database';
import { db } from '../firebase'; // Make sure you have your firebase config file

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    // Save to Firebase
    const messagesRef = ref(db, 'messages');
    push(messagesRef, {
      ...formData,
      timestamp: new Date().toISOString(),
      status: 'unread'
    })
    .then(() => {
      console.log("Form Submitted:", formData);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    })
    .catch((error) => {
      console.error("Error submitting form:", error);
      setError('Failed to send message. Please try again.');
      setIsSubmitting(false);
    });
  };

  return (
    <>
      <style>{`
        /* Base Styles */
        .contact-container {
          padding: 80px 20px;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          font-family: 'Poppins', sans-serif;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .contact-wrapper {
          background: white;
          padding: 50px 40px;
          border-radius: 16px;
          max-width: 700px;
          width: 100%;
          box-shadow: 0 15px 30px rgba(0,0,0,0.1);
          position: relative;
          overflow: hidden;
        }

        .contact-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 8px;
          background: linear-gradient(90deg, #6dd5ed, #2193b0);
        }

        .contact-title {
          text-align: center;
          font-size: 2.5rem;
          font-weight: 700;
          color: #2673c0ff;
          margin-bottom: 40px;
          position: relative;
        }

        .contact-title::after {
          content: '';
          position: absolute;
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 4px;
          background: linear-gradient(to right, #6dd5ed, #2193b0);
          border-radius: 2px;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }

        .form-group {
          position: relative;
        }

        .form-group label {
          position: absolute;
          top: -10px;
          left: 15px;
          background: white;
          padding: 0 8px;
          font-size: 0.9rem;
          color: #3498db;
          font-weight: 500;
        }

        .contact-form input,
        .contact-form textarea {
          width: 100%;
          padding: 16px 20px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 1rem;
          resize: none;
          transition: all 0.3s ease;
          background: #f9f9f9;
        }

        .contact-form input:focus,
        .contact-form textarea:focus {
          border-color: #3498db;
          outline: none;
          background: white;
          box-shadow: 0 5px 15px rgba(52, 152, 219, 0.1);
        }

        .submit-btn {
          background: linear-gradient(to right, #6dd5ed, #2193b0);
          color: white;
          padding: 16px;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .submit-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(33, 147, 176, 0.3);
        }

        .submit-btn:active {
          transform: translateY(0);
        }

        .submit-btn .spinner {
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s ease-in-out infinite;
        }

        .success-message {
          background: #2ecc71;
          color: white;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
          margin-top: 20px;
          animation: fadeIn 0.5s ease;
        }

        .error-message {
          background: #e74c3c;
          color: white;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
          margin-top: 20px;
          animation: fadeIn 0.5s ease;
        }

        /* Animations */
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Responsive Styles */
        @media (max-width: 768px) {
          .contact-wrapper {
            padding: 40px 30px;
          }

          .contact-title {
            font-size: 2rem;
          }
        }

        @media (max-width: 576px) {
          .contact-container {
            padding: 60px 15px;
          }

          .contact-wrapper {
            padding: 30px 20px;
          }

          .contact-title {
            font-size: 1.8rem;
          }

          .contact-form input,
          .contact-form textarea {
            padding: 14px 18px;
          }

          .submit-btn {
            padding: 14px;
            font-size: 1rem;
          }
        }
      `}</style>

      <div className="contact-container">
        <div className="contact-wrapper">
          <h2 className="contact-title">Get In Touch With Us</h2>
          
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="+91 9876543210"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Your Message</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                placeholder="How can we help you?"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </div>
            
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
            
            {submitSuccess && (
              <div className="success-message">
                Thank you! Your message has been sent successfully. We'll contact you soon.
              </div>
            )}
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default ContactUs;